// Stale-bundle recovery.
//
// This SPA code-splits with content-hashed chunk names (e.g.
// `index.83c3b5dd.js`). After a deploy those hashes change, so a browser
// still holding an OLD `index.html` 404s when it tries to fetch a
// now-nonexistent chunk on navigation → the app fails to render and the user
// is left staring at a white screen. We recover by reloading once (or twice,
// across successive deploys) to pull the current bundle.
//
// Two guarantees keep this from becoming an infinite reload loop — the failure
// mode that is *worse* than the white screen it fixes:
//
//  1. We count reloads in sessionStorage and stop after MAX_RELOADS, letting
//     the ErrorBoundary show the real error page instead.
//  2. If sessionStorage is unavailable (some mobile in-app / email webviews,
//     locked-down privacy modes), we CANNOT count reloads, so we do NOT
//     reload at all — an un-guarded reload there loops forever. A single error
//     page is strictly better than an endless blank/flashing screen.
//
// The counter lives in sessionStorage (so it resets on a fresh tab/session),
// and is also reset once the current bundle has run for a short while WITHOUT a
// chunk error — proof it's healthy — so a later deploy can self-heal again. A
// bundle that is still failing never resets the counter, so it can't loop.

const RELOAD_COUNT_KEY = "pp-chunk-reload-count";
const MAX_RELOADS = 2;

// How long the current bundle must run WITHOUT a chunk error before we treat it
// as healthy and re-arm the reload counter (so a later deploy can self-heal).
const REARM_DELAY_MS = 10_000;

// Set for the lifetime of this page load the first time a chunk error is seen.
// Gates the re-arm below: a bundle that has thrown a chunk error is never
// treated as healthy, so a persistently-failing chunk can't re-arm into a loop.
let chunkErrorSeenThisLoad = false;

const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|'text\/html' is not a valid JavaScript MIME type/i;

export const isChunkLoadError = (message?: string | null): boolean =>
  Boolean(message) && CHUNK_ERROR_RE.test(String(message));

// Reads the current reload count. Returns null when sessionStorage can't be
// read — the signal that we must not attempt a (potentially looping) reload.
const readReloadCount = (): number | null => {
  try {
    return Number(sessionStorage.getItem(RELOAD_COUNT_KEY)) || 0;
  } catch {
    return null;
  }
};

// Records that we are about to reload. Returns false when it can't persist the
// count — in which case the caller must NOT reload (we can't guard the loop).
const incrementReloadCount = (current: number): boolean => {
  try {
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(current + 1));
    return true;
  } catch {
    return false;
  }
};

const clearReloadCount = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_COUNT_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
};

// Returns true if it handled a chunk error (and a reload is in flight).
export const recoverFromChunkError = (message?: string | null): boolean => {
  if (!isChunkLoadError(message)) return false;

  chunkErrorSeenThisLoad = true;

  const count = readReloadCount();
  // Storage unavailable → can't guard against an infinite loop → don't reload.
  if (count === null) return false;
  // Already reloaded enough and still failing → give up, let ErrorPage show.
  if (count >= MAX_RELOADS) return false;
  // Couldn't persist the new count → don't risk an unguarded reload loop.
  if (!incrementReloadCount(count)) return false;

  window.location.reload();
  return true;
};

// Wire global listeners. Call once, before rendering.
export const installChunkErrorRecovery = (): void => {
  window.addEventListener("error", (event) => {
    recoverFromChunkError(event.message || event.error?.message);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    recoverFromChunkError(
      typeof reason === "string" ? reason : reason?.message
    );
  });

  // Once the current bundle has run for REARM_DELAY_MS without a single chunk
  // error, treat it as healthy and reset the reload counter so a FUTURE deploy
  // can trigger a fresh one-time reload. Gated on chunkErrorSeenThisLoad, so a
  // bundle that is still failing never re-arms — no periodic reload loop.
  window.addEventListener("load", () => {
    window.setTimeout(() => {
      if (!chunkErrorSeenThisLoad) clearReloadCount();
    }, REARM_DELAY_MS);
  });
};
